import * as React from 'react'
import { ReactElement } from 'react'
import { Text, Tooltip } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { BaseOverviewHeaderItemProps } from '../BaseOverview/BaseOverview'
import BaseOverviewCountBadge from '../BaseOverview/BaseOverviewCountBadge'
import TimeText from '../../components/Text/Time/TimeText'
import { formatTimeInMinutes } from '../../helpers/numberHelper'
import useTasks from '../../hooks/useTasks'
import { ExtendedTaskOverviewDto, TaskFilterType, TaskOverviewCount, TaskSortingType, useItemStyles } from './BaseTaskOverview'
import { ViewType } from '../BaseOverview/BaseOverviewFilter'

/**
 * The header of a task column or list: the number of tasks and, for offices that work with an expected duration, the
 * total of those durations (HAM-4286, criterion 21). The total counts every task of the column, also the ones whose
 * duration is 0:00 and the ones that are hidden per task by the setting.
 */
export default function TaskHeaderItem(
    props: BaseOverviewHeaderItemProps<ExtendedTaskOverviewDto, TaskFilterType, TaskSortingType, TaskOverviewCount>,
): ReactElement {
    const itemStyles = useItemStyles()
    const { t: translate } = useTranslation()
    const { dateConfig } = useTasks()

    const total = dateConfig.duration.enabled ? (
        <Tooltip relationship={'label'} content={translate('task.view.durationtotal')} withArrow>
            <div className={itemStyles.listTaskItemHeaderDuration}>
                <TimeText value={formatTimeInMinutes(props.count?.duration || 0)} />
            </div>
        </Tooltip>
    ) : (
        <div></div>
    )

    // The kanban header is a single line: badge, column name and the total at the end of it.
    if (props.viewType === ViewType.KanbanView) {
        return (
            <>
                <BaseOverviewCountBadge {...props} />
                <Text>
                    {(props.fixedColumnFilterValues?.(props.viewType, props.filterValues) || ([{}] as TaskFilterType[]))[props.columnIndex]?.title}
                </Text>
                {total}
            </>
        )
    }

    // The list header follows the columns of a task row, so the total sits above the durations of the tasks.
    return (
        <>
            <BaseOverviewCountBadge {...props} />
            <div></div>
            <div></div>
            <div className={itemStyles.listTaskItemTitle}>
                {total}
                <div></div>
                <div></div>
            </div>
        </>
    )
}
